import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';

interface SSPDailyData {
  date: string;
  value: number;
}

interface SSPTableProps {
  dailyData: {
    estimatedRevenue: SSPDailyData[];
    adslotImpressions: SSPDailyData[];
    adImpressions: SSPDailyData[];
    winRate: SSPDailyData[];
    estimatedRPM: SSPDailyData[];
  };
  isLoading: boolean;
}

export function SSPTable({ dailyData, isLoading }: SSPTableProps) {
  const { t } = useTranslation();

  // Formater les valeurs
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Combiner toutes les données par date
  const combinedData = React.useMemo(() => {
    if (!dailyData.estimatedRevenue.length) return [];

    return dailyData.estimatedRevenue.map((revenueItem) => {
      const date = revenueItem.date;
      const adslotItem = dailyData.adslotImpressions.find(item => item.date === date);
      const adImpressionsItem = dailyData.adImpressions.find(item => item.date === date);
      const winRateItem = dailyData.winRate.find(item => item.date === date);
      const rpmItem = dailyData.estimatedRPM.find(item => item.date === date);

      return {
        date,
        estimatedRevenue: revenueItem.value,
        adslotImpressions: adslotItem?.value || 0,
        adImpressions: adImpressionsItem?.value || 0,
        winRate: winRateItem?.value || 0,
        estimatedRPM: rpmItem?.value || 0
      };
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [dailyData]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('ssp.table.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Array(7).fill(0).map((_, i) => (
              <div key={i} className="h-12 w-full bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (combinedData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('ssp.table.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            {t('ssp.table.noData')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('ssp.table.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 font-medium text-sm">
                  {t('ssp.table.date')}
                </th>
                <th className="text-right p-3 font-medium text-sm">
                  {t('ssp.estimatedRevenue')}
                </th>
                <th className="text-right p-3 font-medium text-sm">
                  {t('ssp.adslotImpressions')}
                </th>
                <th className="text-right p-3 font-medium text-sm">
                  {t('ssp.adImpressions')}
                </th>
                <th className="text-right p-3 font-medium text-sm">
                  {t('ssp.winRate')}
                </th>
                <th className="text-right p-3 font-medium text-sm">
                  {t('ssp.estimatedRPM')}
                </th>
              </tr>
            </thead>
            <tbody>
              {combinedData.map((row, index) => (
                <tr 
                  key={row.date} 
                  className={`border-b hover:bg-muted/50 ${
                    index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  }`}
                >
                  <td className="p-3 text-sm font-medium">
                    {new Date(row.date).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="p-3 text-sm text-right font-mono">
                    {formatCurrency(row.estimatedRevenue)}
                  </td>
                  <td className="p-3 text-sm text-right font-mono">
                    {formatNumber(row.adslotImpressions)}
                  </td>
                  <td className="p-3 text-sm text-right font-mono">
                    {formatNumber(row.adImpressions)}
                  </td>
                  <td className="p-3 text-sm text-right font-mono">
                    {formatPercentage(row.winRate)}
                  </td>
                  <td className="p-3 text-sm text-right font-mono">
                    {formatCurrency(row.estimatedRPM)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}